import React, { useState, useRef } from "react";
import {
  Flex,
  Select,
  Button,
  DatePicker,
  Space,
  Typography,
  Input,
} from "antd";
import type { Dayjs } from "dayjs";
import dayjs from "dayjs";
import "./index.css";
import { useMediaQuery } from "react-responsive";

const { RangePicker } = DatePicker;
const { Text } = Typography;

interface ListFiltersProps {
  selectedHospital: any;
  availableHospitals: Array<any> | undefined;
  handleHospitalChange: (hospitalId: number) => void;
  handleFiltersChange: (filters: any) => void;
  hideDateFilters?: boolean;
  isShowExtraFilters?: boolean;
}

const ListFilters: React.FC<ListFiltersProps> = ({
  selectedHospital,
  availableHospitals,
  handleHospitalChange,
  handleFiltersChange,
  hideDateFilters,
  isShowExtraFilters,
}) => {
  const [selectedDatePreset, setSelectedDatePreset] = useState<string>("today");
  const [dateRange, setDateRange] = useState<[Dayjs, Dayjs]>([
    dayjs(),
    dayjs(),
  ]);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [missedOnly, setMissedOnly] = useState<boolean>(false);
  const [assignedToMe, setAssignedToMe] = useState<boolean>(false);
  const debounceRef = useRef<NodeJS.Timeout | null>(null);
  const isMobile = useMediaQuery({ maxWidth: 767 });
  const isTablet = useMediaQuery({ minWidth: 768, maxWidth: 1024 });

  const triggerFiltersChange = (extra?: any) => {
    handleFiltersChange({
      startDate: dateRange[0].format("YYYY-MM-DD"),
      endDate: dateRange[1].format("YYYY-MM-DD"),
      searchTerm,
      missedOnly,
      assignedToMe,
      ...extra,
    });
  };

  const handleDateRangePreset = (type: "today" | "tomorrow" | "nextWeek") => {
    setSelectedDatePreset(type);
    let startDate: Dayjs, endDate: Dayjs;

    switch (type) {
      case "today":
        startDate = dayjs();
        endDate = dayjs();
        break;
      case "tomorrow":
        startDate = dayjs().add(1, "day");
        endDate = dayjs().add(1, "day");
        break;
      case "nextWeek":
        startDate = dayjs();
        endDate = dayjs().add(7, "days");
        break;
      default:
        startDate = dayjs();
        endDate = dayjs();
    }

    setDateRange([startDate, endDate]);
    triggerFiltersChange({
      startDate: startDate.format("YYYY-MM-DD"),
      endDate: endDate.format("YYYY-MM-DD"),
    });
  };

  const handleDateRangeChange = (
    dates: [Dayjs | null, Dayjs | null] | null
  ) => {
    if (dates) {
      setSelectedDatePreset("custom");
      setDateRange([dates[0] || dayjs(), dates[1] || dayjs()]);
      triggerFiltersChange({
        startDate: dates[0]?.format("YYYY-MM-DD"),
        endDate: dates[1]?.format("YYYY-MM-DD"),
      });
    }
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchTerm(value);

    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    debounceRef.current = setTimeout(() => {
      triggerFiltersChange({ searchTerm: value });
    }, 300);
  };

  const handleMissedToggle = () => {
    setMissedOnly((prev) => {
      const newMissed = !prev;
      handleFiltersChange({
        startDate: dateRange[0].format("YYYY-MM-DD"),
        endDate: dateRange[1].format("YYYY-MM-DD"),
        searchTerm,
        missedOnly: newMissed,
      });
      return newMissed;
    });
  };

  const handleAssignedToMeToggle = () => {
    setAssignedToMe((prev) => {
      const newAssignedToMe = !prev;
      handleFiltersChange({
        startDate: dateRange[0].format("YYYY-MM-DD"),
        endDate: dateRange[1].format("YYYY-MM-DD"),
        searchTerm,
        assignedToMe: newAssignedToMe,
      });
      return newAssignedToMe;
    });
  };

  return (
    <Flex
      className="appointment-list-filters"
      style={{
        borderRadius: "8px",
        width: "100%",
        display: "flex",
        justifyContent: "space-between",
        flexWrap: "wrap",
      }}
      gap={16}
    >
      <div style={{ display: "flex", gap: "20px", flexWrap: "wrap" }}>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "8px",
          }}
        >
          <Text strong>HOSPITAL</Text>
          <Select
            showSearch
            placeholder="Select Hospital"
            optionFilterProp="label"
            style={{ width: 200 }}
            value={selectedHospital ? selectedHospital.value : undefined}
            options={availableHospitals?.map((hospital) => ({
              label: hospital.label,
              value: hospital.value,
            }))}
            onChange={handleHospitalChange}
            className="nephro-hospital-change"
          />
        </div>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "8px",
          }}
        >
          <Text strong>SEARCH</Text>
          <Input
            placeholder="Search by name, ID, etc."
            value={searchTerm}
            onChange={handleSearchChange}
            allowClear
            style={{ border: "0.4px solid rgb(34, 34, 34)", width: 220 }}
          />
        </div>
        {isShowExtraFilters && (
          <div style={{ display: "flex", alignItems: "end" }}>
            <Button
              type={missedOnly ? "primary" : "default"}
              onClick={handleMissedToggle}
              style={{
                backgroundColor: missedOnly ? "rgb(0, 76, 143)" : undefined,
                color: missedOnly ? "white" : "#575757",
                fontWeight: missedOnly ? "bold" : "normal",
                border: missedOnly
                  ? "1px solid rgb(0, 76, 143)"
                  : "0.4px solid #222222",
                borderRadius: "8px",
              }}
            >
              MISSED ONLY
            </Button>
          </div>
        )}
        {isShowExtraFilters && (
          <div style={{ display: "flex", alignItems: "end" }}>
            <Button
              type={assignedToMe ? "primary" : "default"}
              onClick={handleAssignedToMeToggle}
              style={{
                backgroundColor: assignedToMe ? "rgb(0, 76, 143)" : undefined,
                color: assignedToMe ? "white" : "#575757",
                fontWeight: assignedToMe ? "bold" : "normal",
                border: assignedToMe
                  ? "1px solid rgb(0, 76, 143)"
                  : "0.4px solid #222222",
                borderRadius: "8px",
              }}
            >
              MY CASES
            </Button>
          </div>
        )}
      </div>
      {!hideDateFilters && (
        <Space direction="vertical">
          <Text strong>DATE RANGE</Text>
          <Flex gap={8} style={{ flexWrap: "wrap" }}>
            <Button
              type={selectedDatePreset === "today" ? "primary" : "default"}
              onClick={() => handleDateRangePreset("today")}
              style={{
                backgroundColor:
                  selectedDatePreset === "today"
                    ? "rgb(0, 76, 143)"
                    : undefined,
                color: selectedDatePreset === "today" ? "white" : "#575757",
                fontWeight: selectedDatePreset === "today" ? "bold" : "normal",
                border:
                  selectedDatePreset === "today"
                    ? "1px solid rgb(0, 76, 143)"
                    : "0.4px solid #222222",
                borderRadius: "8px",
              }}
            >
              TODAY
            </Button>
            <Button
              type={selectedDatePreset === "tomorrow" ? "primary" : "default"}
              onClick={() => handleDateRangePreset("tomorrow")}
              style={{
                backgroundColor:
                  selectedDatePreset === "tomorrow"
                    ? "rgb(0, 76, 143)"
                    : undefined,
                color: selectedDatePreset === "tomorrow" ? "white" : "#575757",
                fontWeight:
                  selectedDatePreset === "tomorrow" ? "bold" : "normal",
                border:
                  selectedDatePreset === "tomorrow"
                    ? "1px solid rgb(0, 76, 143)"
                    : "0.4px solid #222222",
                borderRadius: "8px",
              }}
            >
              TOMORROW
            </Button>
            <Button
              type={selectedDatePreset === "nextWeek" ? "primary" : "default"}
              onClick={() => handleDateRangePreset("nextWeek")}
              style={{
                backgroundColor:
                  selectedDatePreset === "nextWeek"
                    ? "rgb(0, 76, 143)"
                    : undefined,
                color: selectedDatePreset === "nextWeek" ? "white" : "#575757",
                fontWeight:
                  selectedDatePreset === "nextWeek" ? "bold" : "normal",
                border:
                  selectedDatePreset === "nextWeek"
                    ? "1px solid rgb(0, 76, 143)"
                    : "0.4px solid #222222",
                borderRadius: "8px",
              }}
            >
              NEXT WEEK
            </Button>
            <RangePicker
              value={dateRange}
              onChange={(dates) => handleDateRangeChange(dates)}
              format="DD-MM-YYYY"
              style={{
                border: "0.4px solid #222222",
                width: "230px",
                borderRadius: "8px",
              }}
              allowClear={true}
            />
          </Flex>
        </Space>
      )}
    </Flex>
  );
};

export default ListFilters;
